import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useAllTimeYearRange } from "../hooks/useAllTimeYearRange";
import { useYearsReportData } from "../hooks/useYearReportData";
import "../css/MonthlyReport.css";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DOW_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const DOW_INDEX_BY_NAME = Object.fromEntries(DOW_NAMES.map((d, i) => [d, i]));
const DOW_ORDER_MON_SUN = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
const WEEKDAYS_MON_THU = ["Monday", "Tuesday", "Wednesday", "Thursday"];
const WEEKEND_FRI_SUN = ["Friday", "Saturday", "Sunday"];

const fmtUSD = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
});

function parseTransactionDate(dateValue) {
  if (!dateValue) return null;
  const parts = String(dateValue).split("/");
  if (parts.length !== 3) return null;

  const m = Number(parts[0]);
  const d = Number(parts[1]);
  const y = Number(parts[2]);
  if (!m || !d || !y) return null;

  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}

function getDowCountsForMonth(year, month) {
  // month: 1-12
  const counts = Array(7).fill(0);
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);
  const d = new Date(start);

  while (d <= end) {
    counts[d.getDay()] += 1;
    d.setDate(d.getDate() + 1);
  }

  return counts;
}

function countDaysInclusive(startDate, endDate) {
  const start = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  const ms = end.getTime() - start.getTime();
  if (Number.isNaN(ms) || ms < 0) return 0;
  return Math.floor(ms / (24 * 60 * 60 * 1000)) + 1;
}

function getPeriodLabel(period) {
  if (!period) return "";
  if (period.kind === "month") {
    return `${monthNames[period.month - 1]} ${period.year}`;
  }
  if (period.kind === "ytd") {
    return `YTD ${period.year} (through ${monthNames[period.throughMonth - 1]})`;
  }
  if (period.kind === "year") {
    return `${period.year} Full Year`;
  }
  return "";
}

function isWeekendDowIndex(dow) {
  // Fri(5) Sat(6) Sun(0)
  return dow === 5 || dow === 6 || dow === 0;
}

function isWeekdayMonThu(dow) {
  // Mon(1) Tue(2) Wed(3) Thu(4)
  return dow >= 1 && dow <= 4;
}

function computeMetricsForPeriod(period, yearData) {
  const empty = {
    totalRevenue: 0,
    dailyPlayRevenue: 0,
    privateEventRevenue: 0,
    weekdayRevenue: 0,
    weekendRevenue: 0,
    avgRevenuePerDay: 0,
    revenueByDow: Object.fromEntries(DOW_NAMES.map((d) => [d, 0])),
    bestDay: { day: "Monday", revenue: 0 },
    worstDay: { day: "Monday", revenue: 0 },
    weekdayAvgDaily: 0,
    weekendAvgDaily: 0,
    weekdayDayCount: 0,
    weekendDayCount: 0,
    dayCount: 0,
  };

  if (!period || !yearData) return empty;

  const txAll = yearData.transactions || [];
  const evAll = yearData.privateEvents || [];

  let monthFilter = null;
  let monthsAllowed = null;
  let startDate = null;
  let endDate = null;

  if (period.kind === "month") {
    monthFilter = period.month;
    startDate = new Date(period.year, period.month - 1, 1);
    endDate = new Date(period.year, period.month, 0);
  } else if (period.kind === "ytd") {
    monthsAllowed = new Set(Array.from({ length: period.throughMonth }, (_, i) => i + 1));
    startDate = new Date(period.year, 0, 1);
    endDate = new Date(period.year, period.throughMonth, 0);
  } else if (period.kind === "year") {
    monthsAllowed = new Set(Array.from({ length: 12 }, (_, i) => i + 1));
    startDate = new Date(period.year, 0, 1);
    endDate = new Date(period.year, 12, 0);
  }

  const includeMonth = (m) => {
    const mm = Number(m);
    if (!Number.isFinite(mm)) return false;
    if (monthFilter != null) return mm === monthFilter;
    if (monthsAllowed) return monthsAllowed.has(mm);
    return true;
  };

  const revenueByDow = Object.fromEntries(DOW_NAMES.map((d) => [d, 0]));
  let dailyPlayRevenue = 0;
  let privateEventRevenue = 0;
  let weekdayRevenue = 0;
  let weekendRevenue = 0;

  const addRevenue = (dateObj, amount) => {
    const amt = Number(amount) || 0;
    const dow = dateObj.getDay();
    const key = DOW_NAMES[dow];
    revenueByDow[key] = (revenueByDow[key] || 0) + amt;
    if (isWeekdayMonThu(dow)) weekdayRevenue += amt;
    if (isWeekendDowIndex(dow)) weekendRevenue += amt;
  };

  for (const t of txAll) {
    if (!includeMonth(t?.Month)) continue;
    const d = parseTransactionDate(t?.Date);
    if (!d) continue;
    const amt = Number(t?.TotalAmount) || 0;
    dailyPlayRevenue += amt;
    addRevenue(d, amt);
  }

  for (const e of evAll) {
    if (!includeMonth(e?.month)) continue;
    if (!e?.startTime) continue;
    const d = new Date(e.startTime);
    if (Number.isNaN(d.getTime())) continue;
    const revenue = (Number(e?.totalPaidAmount) || 0) + (Number(e?.depositAmount) || 0);
    privateEventRevenue += revenue;
    addRevenue(d, revenue);
  }

  const totalRevenue = dailyPlayRevenue + privateEventRevenue;

  const dayCount = startDate && endDate ? countDaysInclusive(startDate, endDate) : 0;
  const avgRevenuePerDay = dayCount ? totalRevenue / dayCount : 0;

  let weekdayDayCount = 0;
  let weekendDayCount = 0;
  if (period.kind === "month") {
    const dowCounts = getDowCountsForMonth(period.year, period.month);
    weekdayDayCount = WEEKDAYS_MON_THU.reduce(
      (s, d) => s + (dowCounts[DOW_INDEX_BY_NAME[d]] || 0),
      0
    );
    weekendDayCount = WEEKEND_FRI_SUN.reduce(
      (s, d) => s + (dowCounts[DOW_INDEX_BY_NAME[d]] || 0),
      0
    );
  }

  const weekdayAvgDaily = weekdayDayCount ? weekdayRevenue / weekdayDayCount : 0;
  const weekendAvgDaily = weekendDayCount ? weekendRevenue / weekendDayCount : 0;

  const pickBestWorst = (daysInOrder) => {
    let bestDay = daysInOrder[0] || "Monday";
    let worstDay = daysInOrder[0] || "Monday";
    for (const d of daysInOrder) {
      if ((revenueByDow[d] || 0) > (revenueByDow[bestDay] || 0)) bestDay = d;
      if ((revenueByDow[d] || 0) < (revenueByDow[worstDay] || 0)) worstDay = d;
    }
    return {
      best: { day: bestDay, revenue: Number(revenueByDow[bestDay] || 0) },
      worst: { day: worstDay, revenue: Number(revenueByDow[worstDay] || 0) },
    };
  };

  const bw = pickBestWorst(DOW_ORDER_MON_SUN);

  return {
    totalRevenue,
    dailyPlayRevenue,
    privateEventRevenue,
    weekdayRevenue,
    weekendRevenue,
    avgRevenuePerDay,
    revenueByDow,
    bestDay: bw.best,
    worstDay: bw.worst,
    weekdayAvgDaily,
    weekendAvgDaily,
    weekdayDayCount,
    weekendDayCount,
    dayCount,
  };
}

function formatPct(value) {
  if (!Number.isFinite(value)) return "—";
  return `${(value * 100).toFixed(1)}%`;
}

function diffStats(a, b) {
  const av = Number(a) || 0;
  const bv = Number(b) || 0;
  const diff = av - bv;
  const pct = bv !== 0 ? diff / bv : av === 0 ? 0 : null;
  const dir = diff > 0 ? "up" : diff < 0 ? "down" : "flat";
  return { diff, pct, dir };
}

const DailyAnalysis = () => {
  const now = new Date();
  const IND_UP = String.fromCharCode(9650);
  const IND_DOWN = String.fromCharCode(9660);
  const IND_FLAT = String.fromCharCode(8211);

  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  const [trendScope, setTrendScope] = useState("year"); // year | allTime
  const {
    range: allTimeRange,
    loading: loadingAllTimeRange,
    err: errAllTimeRange,
  } = useAllTimeYearRange(trendScope === "allTime");

  const [compareMode, setCompareMode] = useState("monthPrev"); // monthPrev | yearPrev | custom
  const [yearCompareScope, setYearCompareScope] = useState("ytd"); // ytd | full
  const [customAMonth, setCustomAMonth] = useState(now.getMonth() + 1);
  const [customAYear, setCustomAYear] = useState(now.getFullYear());
  const [customBMonth, setCustomBMonth] = useState(
    now.getMonth() + 1 === 1 ? 12 : now.getMonth()
  );
  const [customBYear, setCustomBYear] = useState(
    now.getMonth() + 1 === 1 ? now.getFullYear() - 1 : now.getFullYear()
  );

  useEffect(() => {
    if (compareMode !== "custom") return;
    setCustomAMonth(month);
    setCustomAYear(year);
  }, [compareMode, month, year]);

  const currentDate = new Date();
  const isSelectedYearComplete = year < currentDate.getFullYear();

  const prevMonthPeriod = useMemo(() => {
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    return { month: prevMonth, year: prevYear };
  }, [month, year]);

  const periodA = useMemo(() => {
    if (compareMode === "yearPrev") {
      return yearCompareScope === "full" ? { kind: "year", year } : { kind: "ytd", year, throughMonth: month };
    }
    if (compareMode === "custom") return { kind: "month", year: customAYear, month: customAMonth };
    return { kind: "month", year, month };
  }, [compareMode, yearCompareScope, year, month, customAYear, customAMonth]);

  const periodB = useMemo(() => {
    if (compareMode === "yearPrev") {
      const y = year - 1;
      return yearCompareScope === "full" ? { kind: "year", year: y } : { kind: "ytd", year: y, throughMonth: month };
    }
    if (compareMode === "custom") return { kind: "month", year: customBYear, month: customBMonth };
    return { kind: "month", year: prevMonthPeriod.year, month: prevMonthPeriod.month };
  }, [compareMode, yearCompareScope, year, month, customBYear, customBMonth, prevMonthPeriod]);

  const yearsNeeded = useMemo(() => {
    const ys = [year, year - 1, prevMonthPeriod.year, customAYear, customBYear];

    if (
      trendScope === "allTime" &&
      allTimeRange?.startYear &&
      allTimeRange?.endYear
    ) {
      for (let y = allTimeRange.startYear; y <= allTimeRange.endYear; y++) ys.push(y);
    }

    return ys;
  }, [year, prevMonthPeriod.year, customAYear, customBYear, trendScope, allTimeRange]);

  const { byYear, loading: loadingYears, err: errYears } = useYearsReportData(yearsNeeded);
  const requiredYearsLoaded = useMemo(() => {
    const required = new Set([year, periodA?.year, periodB?.year].filter(Boolean));
    for (const y of required) {
      if (!byYear[y]) return false;
    }
    return true;
  }, [byYear, year, periodA, periodB]);
  const loading = !requiredYearsLoaded && !errYears;
  const err = requiredYearsLoaded ? "" : errYears;
  const isDataReady = requiredYearsLoaded && !err;

  const displayUSD = (v) => {
    if (!isDataReady) return "—";
    return fmtUSD.format(Number(v) || 0);
  };

  const displayPct0 = (v) => {
    if (!isDataReady) return "—";
    return `${(Number(v) * 100).toFixed(0)}%`;
  };

  const monthTransactions = useMemo(() => {
    const yd = byYear[year];
    if (!yd) return [];
    return (yd.transactions || []).filter((t) => Number(t?.Month) === Number(month));
  }, [byYear, year, month]);

  const monthPrivateEvents = useMemo(() => {
    const yd = byYear[year];
    if (!yd) return [];
    return (yd.privateEvents || []).filter((e) => Number(e?.month) === Number(month));
  }, [byYear, year, month]);

  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 300);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const prevMonth = () => {
    setMonth((m) => {
      if (m === 1) {
        setYear((y) => y - 1);
        return 12;
      }
      return m - 1;
    });
  };
  const nextMonth = () => {
    setMonth((m) => {
      if (m === 12) {
        setYear((y) => y + 1);
        return 1;
      }
      return m + 1;
    });
  };
  const thisMonth = () => {
    const n = new Date();
    setMonth(n.getMonth() + 1);
    setYear(n.getFullYear());
  };

  // Weekday vs Weekend (Mon-Thu vs Fri-Sun) revenue analysis (Daily Play + Private Events)
  const ww = useMemo(() => {
    if (!isDataReady) {
      const emptyRows = DOW_ORDER_MON_SUN.map((day) => ({ day, revenue: 0 }));
      return {
        rowsMonSun: emptyRows,
        weekdaysRows: WEEKDAYS_MON_THU.map((day) => ({ day, revenue: 0 })),
        weekendRows: WEEKEND_FRI_SUN.map((day) => ({ day, revenue: 0 })),
        totalMonthlyRevenue: 0,
        weekdayRevenue: 0,
        weekendRevenue: 0,
        weekdayAvgDaily: 0,
        weekendAvgDaily: 0,
        weekdayBestWorst: { best: { day: "—", revenue: 0 }, worst: { day: "—", revenue: 0 } },
        weekendBestWorst: { best: { day: "—", revenue: 0 }, worst: { day: "—", revenue: 0 } },
        overallBestWorst: { best: { day: "—", revenue: 0 }, worst: { day: "—", revenue: 0 } },
        weekdayPct: 0,
        weekendPct: 0,
      };
    }

    const revenueByDow = Object.fromEntries(DOW_NAMES.map((d) => [d, 0]));

    const addRevenue = (dayIndex, amount) => {
      const idx = Number(dayIndex);
      if (!Number.isInteger(idx) || idx < 0 || idx > 6) return;
      const key = DOW_NAMES[idx];
      revenueByDow[key] = (revenueByDow[key] || 0) + (Number(amount) || 0);
    };

    for (const t of monthTransactions) {
      const d = parseTransactionDate(t?.Date);
      if (!d) continue;
      addRevenue(d.getDay(), Number(t?.TotalAmount) || 0);
    }

    for (const e of monthPrivateEvents) {
      if (!e?.startTime) continue;
      const d = new Date(e.startTime);
      if (Number.isNaN(d.getTime())) continue;
      const revenue =
        (Number(e?.totalPaidAmount) || 0) + (Number(e?.depositAmount) || 0);
      addRevenue(d.getDay(), revenue);
    }

    const rowsMonSun = DOW_ORDER_MON_SUN.map((day) => ({
      day,
      revenue: Number(revenueByDow[day] || 0),
    }));

    const weekdaysRows = WEEKDAYS_MON_THU.map((day) => ({
      day,
      revenue: Number(revenueByDow[day] || 0),
    }));

    const weekendRows = WEEKEND_FRI_SUN.map((day) => ({
      day,
      revenue: Number(revenueByDow[day] || 0),
    }));

    const totalMonthlyRevenue = rowsMonSun.reduce((s, r) => s + r.revenue, 0);
    const weekdayRevenue = weekdaysRows.reduce((s, r) => s + r.revenue, 0);
    const weekendRevenue = weekendRows.reduce((s, r) => s + r.revenue, 0);

    const dowCounts = getDowCountsForMonth(year, month);
    const weekdayDayCount = WEEKDAYS_MON_THU.reduce(
      (s, d) => s + (dowCounts[DOW_INDEX_BY_NAME[d]] || 0),
      0
    );
    const weekendDayCount = WEEKEND_FRI_SUN.reduce(
      (s, d) => s + (dowCounts[DOW_INDEX_BY_NAME[d]] || 0),
      0
    );

    const weekdayAvgDaily = weekdayDayCount ? weekdayRevenue / weekdayDayCount : 0;
    const weekendAvgDaily = weekendDayCount ? weekendRevenue / weekendDayCount : 0;

    const pickBestWorst = (daysInOrder) => {
      let bestDay = daysInOrder[0] || "";
      let worstDay = daysInOrder[0] || "";
      for (const d of daysInOrder) {
        if ((revenueByDow[d] || 0) > (revenueByDow[bestDay] || 0)) bestDay = d;
        if ((revenueByDow[d] || 0) < (revenueByDow[worstDay] || 0)) worstDay = d;
      }
      return {
        best: { day: bestDay, revenue: Number(revenueByDow[bestDay] || 0) },
        worst: { day: worstDay, revenue: Number(revenueByDow[worstDay] || 0) },
      };
    };

    const weekdayBestWorst = pickBestWorst(WEEKDAYS_MON_THU);
    const weekendBestWorst = pickBestWorst(WEEKEND_FRI_SUN);
    const overallBestWorst = pickBestWorst(DOW_ORDER_MON_SUN);

    const weekdayPct = totalMonthlyRevenue ? weekdayRevenue / totalMonthlyRevenue : 0;
    const weekendPct = totalMonthlyRevenue ? weekendRevenue / totalMonthlyRevenue : 0;

    return {
      rowsMonSun,
      weekdaysRows,
      weekendRows,
      totalMonthlyRevenue,
      weekdayRevenue,
      weekendRevenue,
      weekdayAvgDaily,
      weekendAvgDaily,
      weekdayBestWorst,
      weekendBestWorst,
      overallBestWorst,
      weekdayPct,
      weekendPct,
    };
  }, [monthTransactions, monthPrivateEvents, year, month, isDataReady]);

  const metricsA = useMemo(() => computeMetricsForPeriod(periodA, byYear[periodA?.year]), [periodA, byYear]);
  const metricsB = useMemo(() => computeMetricsForPeriod(periodB, byYear[periodB?.year]), [periodB, byYear]);

  const periodALabel = useMemo(() => getPeriodLabel(periodA), [periodA]);
  const periodBLabel = useMemo(() => getPeriodLabel(periodB), [periodB]);
  const periodBLabelSafe = useMemo(() => {
    if (periodBLabel && periodBLabel === periodALabel) return `${periodBLabel} (B)`;
    return periodBLabel;
  }, [periodALabel, periodBLabel]);

  const formatDiffUSD = (diff) => {
    const d = Number(diff) || 0;
    if (d === 0) return fmtUSD.format(0);
    const sign = d > 0 ? "+" : "-";
    return `${sign}${fmtUSD.format(Math.abs(d))}`;
  };

  const formatPctSigned = (pct) => {
    if (pct == null) return "—";
    if (!Number.isFinite(pct)) return "—";
    if (pct === 0) return "0.0%";
    const sign = pct > 0 ? "+" : "-";
    return `${sign}${formatPct(Math.abs(pct))}`;
  };

  const monthlyTrendYear = useMemo(() => {
    const yd = byYear[year];
    if (!yd) return [];

    const tx = yd.transactions || [];
    const ev = yd.privateEvents || [];
    const totalsByMonth = Array.from({ length: 12 }, () => ({
      label: "",
      revenue: 0,
      dailyPlay: 0,
      privateEvents: 0,
    }));

    for (let i = 0; i < 12; i++) {
      totalsByMonth[i].label = monthNames[i];
    }

    for (const t of tx) {
      const m = Number(t?.Month);
      if (!m || m < 1 || m > 12) continue;
      const amt = Number(t?.TotalAmount) || 0;
      totalsByMonth[m - 1].revenue += amt;
      totalsByMonth[m - 1].dailyPlay += amt;
    }

    for (const e of ev) {
      const m = Number(e?.month);
      if (!m || m < 1 || m > 12) continue;
      const revenue = (Number(e?.totalPaidAmount) || 0) + (Number(e?.depositAmount) || 0);
      totalsByMonth[m - 1].revenue += revenue;
      totalsByMonth[m - 1].privateEvents += revenue;
    }

    return totalsByMonth;
  }, [byYear, year]);

  const monthlyTrendAllTime = useMemo(() => {
    if (trendScope !== "allTime") return [];
    if (!allTimeRange?.startYear || !allTimeRange?.endYear) return [];

    const now = new Date();
    const startYear = Number(allTimeRange.startYear);
    const endYearRaw = Number(allTimeRange.endYear);
    const endYear = Math.min(endYearRaw, now.getFullYear());
    const endMonth = endYear === now.getFullYear() ? now.getMonth() + 1 : 12;

    if (
      !Number.isFinite(startYear) ||
      !Number.isFinite(endYear) ||
      startYear > endYear
    ) {
      return [];
    }

    for (let y = startYear; y <= endYear; y++) {
      if (!byYear[y]) return [];
    }

    const totalsByMonth = [];
    const indexByYearMonth = new Map();

    for (let y = startYear; y <= endYear; y++) {
      const maxMonth = y === endYear ? endMonth : 12;
      for (let m = 1; m <= maxMonth; m++) {
        const idx = totalsByMonth.length;
        indexByYearMonth.set(`${y}-${m}`, idx);
        totalsByMonth.push({
          label: `${monthNames[m - 1].slice(0, 3)} ${y}`,
          revenue: 0,
          dailyPlay: 0,
          privateEvents: 0,
        });
      }
    }

    for (let y = startYear; y <= endYear; y++) {
      const yd = byYear[y];
      if (!yd) continue;

      for (const t of yd.transactions || []) {
        const m = Number(t?.Month);
        if (!m || m < 1 || m > 12) continue;
        const idx = indexByYearMonth.get(`${y}-${m}`);
        if (idx == null) continue;
        const amt = Number(t?.TotalAmount) || 0;
        totalsByMonth[idx].revenue += amt;
        totalsByMonth[idx].dailyPlay += amt;
      }

      for (const e of yd.privateEvents || []) {
        const m = Number(e?.month);
        if (!m || m < 1 || m > 12) continue;
        const idx = indexByYearMonth.get(`${y}-${m}`);
        if (idx == null) continue;
        const revenue =
          (Number(e?.totalPaidAmount) || 0) + (Number(e?.depositAmount) || 0);
        totalsByMonth[idx].revenue += revenue;
        totalsByMonth[idx].privateEvents += revenue;
      }
    }

    return totalsByMonth;
  }, [trendScope, allTimeRange, byYear]);

  const monthlyTrend = useMemo(() => {
    return trendScope === "allTime" ? monthlyTrendAllTime : monthlyTrendYear;
  }, [trendScope, monthlyTrendAllTime, monthlyTrendYear]);

  const monthlyTrendXAxisInterval = useMemo(() => {
    if (trendScope !== "allTime") return 0;
    const len = monthlyTrend.length;
    if (!len) return 0;
    const desiredTicks = 12;
    return Math.max(0, Math.ceil(len / desiredTicks) - 1);
  }, [trendScope, monthlyTrend]);

  const allTimeTrendError = useMemo(() => {
    if (trendScope !== "allTime") return "";
    return errAllTimeRange || errYears;
  }, [trendScope, errAllTimeRange, errYears]);

  const allTimeTrendLoading = useMemo(() => {
    if (trendScope !== "allTime") return false;
    if (allTimeTrendError) return false;
    if (loadingAllTimeRange || !allTimeRange) return true;
    return loadingYears || !monthlyTrendAllTime.length;
  }, [
    trendScope,
    allTimeTrendError,
    loadingAllTimeRange,
    allTimeRange,
    loadingYears,
    monthlyTrendAllTime,
  ]);

  return (
    <div className="monthly-report-container">
      <div className="monthly-report-header">
        <h2 className="monthly-report-title">Daily Analysis</h2>

        <div className="header-actions">
          <button
            className="btn ghost"
            onClick={prevMonth}
            aria-label="Previous month"
          >
             ◀
          </button>
          <div className="filters">
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              aria-label="Month"
            >
              {monthNames.map((name, idx) => (
                <option key={name} value={idx + 1}>
                  {name}
                </option>
              ))}
            </select>
            <input
              type="number"
              min="2000"
              max={new Date().getFullYear() + 1}
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              aria-label="Year"
            />
          </div>
          <button className="btn ghost" onClick={nextMonth} aria-label="Next month">
             ▶
          </button>
        </div>

        <div className="bar-actions">
          <button className="btn" onClick={thisMonth}>
            This Month
          </button>
          <button className="btn" onClick={() => window.print()}>
            Print
          </button>
        </div>
      </div>

      {err ? (
        <div className="error">{err}</div>
      ) : (
        <>
          <div className="analysis-section">
            <div className="analysis-header">
              <h3 className="analysis-title">Weekday vs Weekend Analysis</h3>
              <div className="analysis-subtitle">
                Includes Daily Play + Private Event revenue for {monthNames[month - 1]}{" "}
                {year}
              </div>
            </div>

            {loading && <div className="analysis-inline-loading">Updating…</div>}

            <div className="analysis-grid">
              <div className="analysis-card">
                <h4 className="analysis-card-title">Weekdays (Mon-Thu)</h4>

                <div className="analysis-kpis">
                  <div className="analysis-kpi">
                    <div className="analysis-kpi-label">Total Revenue</div>
                    <div className="analysis-kpi-value">
                      {displayUSD(ww.weekdayRevenue)}
                    </div>
                  </div>
                  <div className="analysis-kpi">
                    <div className="analysis-kpi-label">Average Revenue Per Day</div>
                    <div className="analysis-kpi-value">
                      {displayUSD(ww.weekdayAvgDaily)}
                    </div>
                  </div>
                  <div className="analysis-kpi">
                    <div className="analysis-kpi-label">Highest Revenue Day</div>
                    <div className="analysis-kpi-value">
                      {isDataReady
                        ? `${ww.weekdayBestWorst.best.day} (${displayUSD(
                            ww.weekdayBestWorst.best.revenue
                          )})`
                        : "—"}
                    </div>
                  </div>
                  <div className="analysis-kpi">
                    <div className="analysis-kpi-label">Lowest Revenue Day</div>
                    <div className="analysis-kpi-value">
                      {isDataReady
                        ? `${ww.weekdayBestWorst.worst.day} (${displayUSD(
                            ww.weekdayBestWorst.worst.revenue
                          )})`
                        : "—"}
                    </div>
                  </div>
                </div>

                <div className="analysis-table-wrap">
                  <table className="analysis-table">
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th className="td-right">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ww.weekdaysRows.map((r) => (
                        <tr key={r.day}>
                          <td>{r.day}</td>
                          <td className="td-right">{displayUSD(r.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="analysis-card">
                <h4 className="analysis-card-title">Weekend (Fri-Sun)</h4>

                <div className="analysis-kpis">
                  <div className="analysis-kpi">
                    <div className="analysis-kpi-label">Total Revenue</div>
                    <div className="analysis-kpi-value">
                      {displayUSD(ww.weekendRevenue)}
                    </div>
                  </div>
                  <div className="analysis-kpi">
                    <div className="analysis-kpi-label">Average Revenue Per Day</div>
                    <div className="analysis-kpi-value">
                      {displayUSD(ww.weekendAvgDaily)}
                    </div>
                  </div>
                  <div className="analysis-kpi">
                    <div className="analysis-kpi-label">Highest Revenue Day</div>
                    <div className="analysis-kpi-value">
                      {isDataReady
                        ? `${ww.weekendBestWorst.best.day} (${displayUSD(
                            ww.weekendBestWorst.best.revenue
                          )})`
                        : "—"}
                    </div>
                  </div>
                  <div className="analysis-kpi">
                    <div className="analysis-kpi-label">Lowest Revenue Day</div>
                    <div className="analysis-kpi-value">
                      {isDataReady
                        ? `${ww.weekendBestWorst.worst.day} (${displayUSD(
                            ww.weekendBestWorst.worst.revenue
                          )})`
                        : "—"}
                    </div>
                  </div>
                </div>

                <div className="analysis-table-wrap">
                  <table className="analysis-table">
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th className="td-right">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ww.weekendRows.map((r) => (
                        <tr key={r.day}>
                          <td>{r.day}</td>
                          <td className="td-right">{displayUSD(r.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="analysis-card analysis-card-full">
              <h4 className="analysis-card-title">Additional Metrics</h4>
              <div className="analysis-kpis analysis-kpis-wide">
                <div className="analysis-kpi">
                  <div className="analysis-kpi-label">Total Monthly Revenue</div>
                  <div className="analysis-kpi-value">
                    {displayUSD(ww.totalMonthlyRevenue)}
                  </div>
                </div>
                <div className="analysis-kpi">
                  <div className="analysis-kpi-label">Weekday Revenue Percentage</div>
                  <div className="analysis-kpi-value">
                    {displayPct0(ww.weekdayPct)}
                  </div>
                </div>
                <div className="analysis-kpi">
                  <div className="analysis-kpi-label">Weekend Revenue Percentage</div>
                  <div className="analysis-kpi-value">
                    {displayPct0(ww.weekendPct)}
                  </div>
                </div>
                <div className="analysis-kpi">
                  <div className="analysis-kpi-label">
                    Difference Between Weekend and Weekday Daily Average
                  </div>
                  <div className="analysis-kpi-value">
                    {isDataReady
                      ? displayUSD(ww.weekendAvgDaily - ww.weekdayAvgDaily)
                      : "—"}
                  </div>
                </div>
                <div className="analysis-kpi">
                  <div className="analysis-kpi-label">Best Performing Day Of Week</div>
                  <div className="analysis-kpi-value">
                    {isDataReady
                      ? `${ww.overallBestWorst.best.day} (${displayUSD(
                          ww.overallBestWorst.best.revenue
                        )})`
                      : "—"}
                  </div>
                </div>
                <div className="analysis-kpi">
                  <div className="analysis-kpi-label">Worst Performing Day Of Week</div>
                  <div className="analysis-kpi-value">
                    {isDataReady
                      ? `${ww.overallBestWorst.worst.day} (${displayUSD(
                          ww.overallBestWorst.worst.revenue
                        )})`
                      : "—"}
                  </div>
                </div>
              </div>

              <div className="analysis-insights">
                {isDataReady ? (
                  <>
                    <p>
                      Weekend revenue represents {displayPct0(ww.weekendPct)} of total
                      monthly revenue.
                    </p>
                    <p>
                      {ww.overallBestWorst.best.day} is the highest performing day with{" "}
                      {displayUSD(ww.overallBestWorst.best.revenue)} revenue.
                    </p>
                  </>
                ) : (
                  <p>Insights update once data is ready.</p>
                )}
              </div>
            </div>

            <div className="analysis-grid">
              <div className="analysis-card">
                <h4 className="analysis-card-title">Revenue By Day Of Week</h4>
                <div className="analysis-chart">
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={ww.rowsMonSun} margin={{ bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="day"
                        interval={0}
                        angle={-25}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis />
                      <Tooltip
                        formatter={(v) => fmtUSD.format(Number(v) || 0)}
                        labelFormatter={(label) => String(label)}
                      />
                      <Bar dataKey="revenue" name="Revenue" fill="#3499c8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="analysis-card">
                <h4 className="analysis-card-title">Weekday vs Weekend Comparison</h4>
                <div className="analysis-chart">
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart
                      data={[
                        {
                          metric: "Total Revenue",
                          Weekdays: ww.weekdayRevenue,
                          Weekend: ww.weekendRevenue,
                        },
                        {
                          metric: "Avg Daily Revenue",
                          Weekdays: ww.weekdayAvgDaily,
                          Weekend: ww.weekendAvgDaily,
                        },
                      ]}
                      margin={{ bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="metric" interval={0} />
                      <YAxis />
                      <Tooltip
                        formatter={(v) => fmtUSD.format(Number(v) || 0)}
                        labelFormatter={(label) => String(label)}
                      />
                      <Legend />
                      <Bar dataKey="Weekdays" fill="#2563eb" />
                      <Bar dataKey="Weekend" fill="#f59e0b" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          <div className="analysis-section">
            <div className="analysis-header">
              <h3 className="analysis-title">Trend Comparison</h3>
              <div className="analysis-subtitle">
                Compare two periods (Daily Play + Private Events) using the same month/year
                filters above.
              </div>
            </div>

            {loading && <div className="analysis-inline-loading">Updating…</div>}

            <div className="analysis-card analysis-card-full">
              <h4 className="analysis-card-title">Comparison Controls</h4>
              <div className="comparison-controls">
                <div className="comparison-control">
                  <div className="comparison-label">Compare</div>
                  <select
                    value={compareMode}
                    onChange={(e) => setCompareMode(e.target.value)}
                    aria-label="Comparison mode"
                  >
                    <option value="monthPrev">Current month vs previous month</option>
                    <option value="yearPrev">Current year vs previous year</option>
                    <option value="custom">Custom month comparison</option>
                  </select>
                </div>

                {compareMode === "yearPrev" && (
                  <div className="comparison-control">
                    <div className="comparison-label">Year Scope</div>
                    <select
                      value={yearCompareScope}
                      onChange={(e) => setYearCompareScope(e.target.value)}
                      aria-label="Year comparison scope"
                    >
                      <option value="ytd">
                        Year-to-date (through selected month)
                      </option>
                      <option value="full">
                        Full year (Jan-Dec)
                      </option>
                    </select>
                    {!isSelectedYearComplete && yearCompareScope === "full" && (
                      <div className="comparison-hint">
                        Full-year comparison includes future months for {year}. Months with no
                        data count as $0.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {compareMode === "custom" && (
                <div className="comparison-custom">
                  <div className="comparison-custom-col">
                    <div className="comparison-custom-title">Period A</div>
                    <div className="filters">
                      <select
                        value={customAMonth}
                        onChange={(e) => setCustomAMonth(Number(e.target.value))}
                        aria-label="Month A"
                      >
                        {monthNames.map((name, idx) => (
                          <option key={`a-${name}`} value={idx + 1}>
                            {name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="2000"
                        max={new Date().getFullYear() + 1}
                        value={customAYear}
                        onChange={(e) => setCustomAYear(Number(e.target.value))}
                        aria-label="Year A"
                        style={{ width: 110 }}
                      />
                    </div>
                  </div>

                  <div className="comparison-custom-col">
                    <div className="comparison-custom-title">Period B</div>
                    <div className="filters">
                      <select
                        value={customBMonth}
                        onChange={(e) => setCustomBMonth(Number(e.target.value))}
                        aria-label="Month B"
                      >
                        {monthNames.map((name, idx) => (
                          <option key={`b-${name}`} value={idx + 1}>
                            {name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="2000"
                        max={new Date().getFullYear() + 1}
                        value={customBYear}
                        onChange={(e) => setCustomBYear(Number(e.target.value))}
                        aria-label="Year B"
                        style={{ width: 110 }}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="comparison-summary">
                <strong>Period A:</strong> {periodALabel} &nbsp;|&nbsp;{" "}
                <strong>Period B:</strong> {periodBLabelSafe}
              </div>
            </div>

            <div className="comparison-metrics">
              {(() => {
                const cards = [
                  {
                    key: "totalRevenue",
                    title: "Total Revenue",
                    a: metricsA.totalRevenue,
                    b: metricsB.totalRevenue,
                    format: (v) => fmtUSD.format(v),
                  },
                  {
                    key: "dailyPlayRevenue",
                    title: "Daily Play Revenue",
                    a: metricsA.dailyPlayRevenue,
                    b: metricsB.dailyPlayRevenue,
                    format: (v) => fmtUSD.format(v),
                  },
                  {
                    key: "privateEventRevenue",
                    title: "Private Event Revenue",
                    a: metricsA.privateEventRevenue,
                    b: metricsB.privateEventRevenue,
                    format: (v) => fmtUSD.format(v),
                  },
                  {
                    key: "weekdayRevenue",
                    title: "Weekday Revenue (Mon-Thu)",
                    a: metricsA.weekdayRevenue,
                    b: metricsB.weekdayRevenue,
                    format: (v) => fmtUSD.format(v),
                  },
                  {
                    key: "weekendRevenue",
                    title: "Weekend Revenue (Fri-Sun)",
                    a: metricsA.weekendRevenue,
                    b: metricsB.weekendRevenue,
                    format: (v) => fmtUSD.format(v),
                  },
                  {
                    key: "avgRevenuePerDay",
                    title: "Average Revenue Per Day",
                    a: metricsA.avgRevenuePerDay,
                    b: metricsB.avgRevenuePerDay,
                    format: (v) => fmtUSD.format(v),
                  },
                  {
                    key: "bestDay",
                    title: "Best Performing Day",
                    a: metricsA.bestDay?.revenue || 0,
                    b: metricsB.bestDay?.revenue || 0,
                    labelA: `${metricsA.bestDay?.day || ""} (${fmtUSD.format(
                      metricsA.bestDay?.revenue || 0
                    )})`,
                    labelB: `${metricsB.bestDay?.day || ""} (${fmtUSD.format(
                      metricsB.bestDay?.revenue || 0
                    )})`,
                    format: (v) => fmtUSD.format(v),
                  },
                  {
                    key: "worstDay",
                    title: "Worst Performing Day",
                    a: metricsA.worstDay?.revenue || 0,
                    b: metricsB.worstDay?.revenue || 0,
                    labelA: `${metricsA.worstDay?.day || ""} (${fmtUSD.format(
                      metricsA.worstDay?.revenue || 0
                    )})`,
                    labelB: `${metricsB.worstDay?.day || ""} (${fmtUSD.format(
                      metricsB.worstDay?.revenue || 0
                    )})`,
                    format: (v) => fmtUSD.format(v),
                  },
                ];

                return cards.map((c) => {
                  const s = diffStats(c.a, c.b);
                  const indicator =
                    s.dir === "up" ? IND_UP : s.dir === "down" ? IND_DOWN : IND_FLAT;
                  const valueA = c.labelA ?? c.format(c.a);
                  const valueB = c.labelB ?? c.format(c.b);

                  return (
                    <div key={c.key} className="analysis-kpi comparison-card">
                      <div className="comparison-card-header">
                        <div className="analysis-kpi-label">{c.title}</div>
                        <div className={`trend-indicator ${s.dir}`}>
                          {indicator}
                        </div>
                      </div>
                      <div className="comparison-card-values">
                        <div className="comparison-card-row">
                          <div className="comparison-card-period">{periodALabel}</div>
                          <div className="analysis-kpi-value">{valueA}</div>
                        </div>
                        <div className="comparison-card-row">
                          <div className="comparison-card-period">{periodBLabelSafe}</div>
                          <div className="analysis-kpi-value">{valueB}</div>
                        </div>
                      </div>
                      <div className="comparison-card-delta">
                        <div className={`comparison-diff ${s.dir}`}>
                          Difference: {formatDiffUSD(s.diff)}
                        </div>
                        <div className={`comparison-pct ${s.dir}`}>
                          Change: {formatPctSigned(s.pct)}
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>

            <div className="analysis-grid">
              <div className="analysis-card">
                <h4 className="analysis-card-title">Month Comparison</h4>
                <div className="analysis-chart">
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart
                      data={[
                        { period: periodALabel, revenue: metricsA.totalRevenue },
                        { period: periodBLabelSafe, revenue: metricsB.totalRevenue },
                      ]}
                      margin={{ bottom: 24 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="period"
                        interval={0}
                        angle={-15}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis />
                      <Tooltip
                        formatter={(v) => fmtUSD.format(Number(v) || 0)}
                        labelFormatter={(label) => String(label)}
                      />
                      <Bar dataKey="revenue" name="Revenue" fill="#3499c8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="analysis-card">
                <h4 className="analysis-card-title">Revenue Breakdown Comparison</h4>
                <div className="analysis-chart">
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart
                      data={[
                        {
                          metric: "Daily Play",
                          [periodALabel]: metricsA.dailyPlayRevenue,
                          [periodBLabelSafe]: metricsB.dailyPlayRevenue,
                        },
                        {
                          metric: "Private Events",
                          [periodALabel]: metricsA.privateEventRevenue,
                          [periodBLabelSafe]: metricsB.privateEventRevenue,
                        },
                        {
                          metric: "Weekdays (Mon-Thu)",
                          [periodALabel]: metricsA.weekdayRevenue,
                          [periodBLabelSafe]: metricsB.weekdayRevenue,
                        },
                        {
                          metric: "Weekend (Fri-Sun)",
                          [periodALabel]: metricsA.weekendRevenue,
                          [periodBLabelSafe]: metricsB.weekendRevenue,
                        },
                      ]}
                      margin={{ bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="metric" interval={0} />
                      <YAxis />
                      <Tooltip
                        formatter={(v) => fmtUSD.format(Number(v) || 0)}
                        labelFormatter={(label) => String(label)}
                      />
                      <Legend />
                      <Bar dataKey={periodALabel} fill="#2563eb" />
                      <Bar dataKey={periodBLabelSafe} fill="#f59e0b" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="analysis-card analysis-card-full">
              <h4 className="analysis-card-title">
                Monthly Trend {trendScope === "allTime" ? "(All Time)" : `(${year})`}
              </h4>

              <div className="comparison-controls" style={{ marginBottom: 6 }}>
                <div className="comparison-control" style={{ minWidth: 220 }}>
                  <div className="comparison-label">Scope</div>
                  <select
                    value={trendScope}
                    onChange={(e) => setTrendScope(e.target.value)}
                    aria-label="Monthly trend scope"
                  >
                    <option value="year">Selected year</option>
                    <option value="allTime">All time</option>
                  </select>
                </div>
              </div>

              {trendScope === "allTime" && allTimeTrendError ? (
                <div className="error">{allTimeTrendError}</div>
              ) : trendScope === "allTime" && allTimeTrendLoading ? (
                <div className="analysis-chart">
                  <div className="loading" style={{ height: 340, display: "grid", placeItems: "center" }}>
                    Loading all-time trend…
                  </div>
                </div>
              ) : (
                <div className="analysis-chart">
                  <ResponsiveContainer width="100%" height={340}>
                    <LineChart data={monthlyTrend} margin={{ bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="label"
                        interval={monthlyTrendXAxisInterval}
                        angle={-25}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis />
                      <Tooltip
                        formatter={(v) => fmtUSD.format(Number(v) || 0)}
                        labelFormatter={(label) => String(label)}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        name="Total Revenue"
                        stroke="#3499c8"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="dailyPlay"
                        name="Daily Play"
                        stroke="#2563eb"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="privateEvents"
                        name="Private Events"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {showTop && (
        <button
          className="back-to-top"
          onClick={scrollToTop}
          aria-label="Back to top"
          title="Back to top"
        >
          â†‘
        </button>
      )}
    </div>
  );
};

export default DailyAnalysis;
